import { useEffect, useState } from 'react';
import { useTranslate } from 'react-admin';
import { fNumber, fShortenNumber } from 'src/utils/format-number';
import { ColorType } from 'src/theme/core';
import { baseUrl, httpClient } from '../../../providers/AppDataProvider';
import { PredictionDashboardFilter } from '../dataProvider';
import { CardGrid } from './CardGrid';
import { ItemCard } from './ItemCard';

const icon = {
  revenue: 'heroicons-outline:currency-dollar',
  profit: 'flowbite:chart-mixed-dollar-solid',
  user: 'uil:user',
  deposit: 'flowbite:arrow-down-to-bracket-solid',
  withdraw: 'mdi:bank-transfer-out',
} as const;

const unitKey = {
  currency: 'module.dashBoard.unitCurrency',
  user: 'module.dashBoard.unitUsers',
} as const;

type TapGridStats = {
  activeUsers?: number | string;
  tradingVolume?: number | string;
  revenue?: number | string;
  newUsers?: number | string;
  depositVolume?: number | string;
  withdrawVolume?: number | string;
};

const toIsoString = (value?: string | number) => {
  if (value === undefined || value === null || value === '') return undefined;
  if (typeof value === 'number') return new Date(value).toISOString();
  const timestamp = Number(value);
  if (!Number.isNaN(timestamp) && value.trim() !== '' && /^\d+$/.test(value.trim())) {
    return new Date(timestamp).toISOString();
  }
  return value;
};

const useTapGridStats = (filter: PredictionDashboardFilter) => {
  const [stats, setStats] = useState<TapGridStats | null>(null);
  const [loading, setLoading] = useState(true);

  const from = toIsoString(filter.fromTime);
  const to = toIsoString(filter.toTime);

  useEffect(() => {
    let mounted = true;
    const queryParams = new URLSearchParams();
    if (from && to) {
      queryParams.set('period', 'CUSTOM');
      queryParams.set('from', from);
      queryParams.set('to', to);
    } else {
      queryParams.set('period', 'ALL_TIME');
    }

    setLoading(true);
    httpClient(`${baseUrl}/tap-grid/stats?${queryParams.toString()}`, { method: 'GET' })
      .then(({ json }) => {
        if (mounted) setStats((json?.data ?? json ?? {}) as TapGridStats);
      })
      .catch(() => {
        if (mounted) setStats({});
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [from, to]);

  return { stats, loading };
};

type TapGridCardConfig = {
  title: string;
  tooltip: string;
  icon: string;
  unitKey: string;
  color: ColorType;
  value: number;
};

export const TapGridListCards = ({ filter }: { filter: PredictionDashboardFilter }) => {
  const translate = useTranslate();
  const { stats, loading } = useTapGridStats(filter);

  const renderCard = ({
    title,
    tooltip,
    icon: cardIcon,
    unitKey: cardUnitKey,
    color,
    value,
  }: TapGridCardConfig) => (
    <ItemCard
      title={translate(title)}
      total={`${translate(cardUnitKey)} ${fShortenNumber(value)}`}
      totalTooltip={`${translate(cardUnitKey)} ${fNumber(value, { maximumFractionDigits: 2 })}`}
      color={color}
      icon={cardIcon}
      tooltip={translate(tooltip)}
      isValueLoading={loading}
    />
  );

  const num = (value?: number | string) => Number(value ?? 0);

  return (
    <>
      <CardGrid>
        {renderCard({
          title: 'module.dashBoard.newUsers',
          tooltip: 'module.dashBoard.newUsersTooltip',
          icon: icon.user,
          unitKey: unitKey.user,
          color: 'error',
          value: num(stats?.newUsers),
        })}
        {renderCard({
          title: 'module.dashBoard.deposit',
          tooltip: 'module.dashBoard.depositTooltip',
          icon: icon.deposit,
          unitKey: unitKey.currency,
          color: 'success',
          value: num(stats?.depositVolume),
        })}
        {renderCard({
          title: 'module.dashBoard.revenue',
          tooltip: 'module.dashBoard.revenueTooltip',
          icon: icon.revenue,
          unitKey: unitKey.currency,
          color: 'warning',
          value: num(stats?.revenue),
        })}
        {renderCard({
          title: 'module.dashBoard.activeTraders',
          tooltip: 'module.dashBoard.activeTradersTooltip',
          icon: icon.user,
          unitKey: unitKey.user,
          color: 'info',
          value: num(stats?.activeUsers),
        })}
        {renderCard({
          title: 'module.dashBoard.withdraw',
          tooltip: 'module.dashBoard.withdrawTooltip',
          icon: icon.withdraw,
          unitKey: unitKey.currency,
          color: 'error',
          value: num(stats?.withdrawVolume),
        })}
        {renderCard({
          title: 'module.dashBoard.tradingStats',
          tooltip: 'module.dashBoard.tradingStatsTooltip',
          icon: icon.profit,
          unitKey: unitKey.currency,
          color: 'info',
          value: num(stats?.tradingVolume),
        })}
      </CardGrid>
    </>
  );
};
